/**
 * Cards "Biomicroscopia" e "Fundoscopia" do ditado (pedido do Leopoldo,
 * 14/10/2026).
 *
 * Os dois aparecem SEMPRE na coluna do ditado e, em cada olho, oferecem a
 * descrição normal pra marcar com um clique — o médico deixa de ditar o
 * normal toda consulta. Não são um tipo novo de documento: o que é marcado
 * vira a célula `achados` daquele olho no card "Exames do atendimento", o
 * mesmo lugar onde cai o achado ditado. Dali vai pros módulos da ficha e pro
 * "Prontuário" pelo caminho de sempre. Nada marcado = nada gravado.
 *
 * Achado DITADO para um olho prevalece sobre o normal daquele olho
 * (`mergeExamFill` troca o normal pelo ditado em vez de fundir). Normal dito
 * COM exceção ("normal, exceto pela escavação aumentada") vira este texto
 * com a exceção no lugar da estrutura (`normalWithException`, 25/10/2026).
 */

import type { ExamFillMap } from '@/utils/encounterAutoFill';

// Mapeamento de retina entrou em 26/10/2026, com o mesmo card.
export const NORMAL_EXAM_MODULES = ['biomicroscopia', 'fundoscopia', 'mapeamento-retina'] as const;
export type NormalExamModule = (typeof NORMAL_EXAM_MODULES)[number];
export type NormalEye = 'OD' | 'OE';

/** Campo dos dois módulos (`encounterCatalog`): texto livre por olho. */
export const NORMAL_FIELD = 'achados';

/** Texto do normal, como o Leopoldo pediu (14/10/2026). */
export const NORMAL_FINDING: Record<NormalExamModule, string> = {
  biomicroscopia: 'Conjuntiva calma, córnea transparente, câmara anterior ampla, íris trófica, fácico.',
  fundoscopia: 'Disco óptico normocorado, escavação papilar com boa rima neurorretiniana, mácula e vasos sem alterações.',
  'mapeamento-retina': 'Vítreo transparente, retina aplicada em 360°, sem roturas, sem degenerações periféricas.',
};

export const NORMAL_EXAM_LABEL: Record<NormalExamModule, string> = {
  biomicroscopia: 'Biomicroscopia',
  fundoscopia: 'Fundoscopia',
  'mapeamento-retina': 'Mapeamento de retina',
};

const comparavel = (s: string) => s.trim().replace(/[.\s]+$/, '').toLowerCase();

export function isNormalExamModule(moduleId: string): moduleId is NormalExamModule {
  return (NORMAL_EXAM_MODULES as readonly string[]).includes(moduleId);
}

/** O texto é o normal marcado no card (não um achado ditado)? */
export function isNormalFinding(moduleId: string, text: string | undefined): boolean {
  if (!text || !isNormalExamModule(moduleId)) return false;
  return comparavel(text) === comparavel(NORMAL_FINDING[moduleId]);
}

const semAcento = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Nome do exame e o olho, que o modelo às vezes repete no valor ("biomicroscopia normal AO", "o fundo do olho tá normal"). */
const MOLDURA = /\b(biomicroscopia|fundoscopia|fundo de olho|fundo do olho|mapeamento de retina|mapeamento da retina|mapeamento|exame|em ambos os olhos|nos dois olhos|em ambos|ambos os olhos|ambos|bilateral|ao|od|oe|esta|ta|e|o|a)\b/g;
const DITO_NORMAL = /^(normal|normais|sem alteracoes|sem alteracao|sem particularidades|nda|dentro da normalidade|dentro dos limites da normalidade|sem achados)$/;

/**
 * "Biomicroscopia e fundoscopia normais" (pedido de 16/10/2026): o ditado do
 * normal marca a caixa do card nos olhos ditos, igual ao clique — vira o
 * texto normal da clínica. Achado de verdade ("córnea transparente") passa
 * intacto.
 */
export function saidNormalToFinding(moduleId: string, text: string): string {
  if (!isNormalExamModule(moduleId)) return text;
  const resto = semAcento(text).replace(/[.,;:!]/g, ' ').replace(MOLDURA, ' ').replace(/\s+/g, ' ').trim();
  return DITO_NORMAL.test(resto) ? NORMAL_FINDING[moduleId] : text;
}

/** O que o card mostra num olho: o normal marcado, um achado ditado, ou nada. */
export function eyeFinding(
  fill: ExamFillMap,
  moduleId: NormalExamModule,
  eye: NormalEye,
): { normal: boolean; ditado: string | null } {
  const texto = fill[moduleId]?.[NORMAL_FIELD]?.[eye]?.trim();
  if (!texto) return { normal: false, ditado: null };
  return isNormalFinding(moduleId, texto) ? { normal: true, ditado: null } : { normal: false, ditado: texto };
}

/**
 * A célula pertence a um destes cards (e portanto não se repete no card
 * "Exames do atendimento").
 */
export function isNormalCardField(moduleId: string, key: string): boolean {
  return isNormalExamModule(moduleId) && key === NORMAL_FIELD;
}
